// --- NEW FILE: packages/agent-kit/src/hooks/tasks/useManageTask.ts ---

import { useState, useCallback } from 'react';
import { adminTaskService, isApiError } from '@dopomogai/api-client';
import type { ProjectTask, TaskPayload, UUID } from '@dopomogai/types';
import { AgentKitError } from '../../types';

/**
 * A hook for managing the lifecycle of tasks (create, update).
 * It provides the functions that components will use to mutate task data.
 */
export const useManageTask = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);

  /**
   * Creates a new task within a project's global backlog.
   * @param projectId The ID of the project that owns the task.
   * @param taskData The multilingual payload for the new task.
   * @returns The newly created ProjectTask object, or null on failure.
   */
  const createTask = useCallback(async (projectId: UUID, taskData: TaskPayload): Promise<ProjectTask | null> => {
    setIsLoading(true);
    setError(null);
    const res = await adminTaskService.createTask(projectId, taskData);
    if (isApiError(res)) {
      setError(new AgentKitError('Failed to create task', res));
      setIsLoading(false);
      return null;
    }
    setIsLoading(false);
    return res;
  }, []);

  /**
   * Updates an existing task. The backend will handle patching the correct language pack.
   * @param taskId The ID of the task to update.
   * @param taskData The multilingual payload for the task update.
   * @returns The updated ProjectTask object, or null on failure.
   */
  const updateTask = useCallback(async (taskId: string, taskData: TaskPayload): Promise<ProjectTask | null> => {
    setIsLoading(true);
    setError(null);
    const res = await adminTaskService.updateTask(taskId, taskData);
    if (isApiError(res)) {
      setError(new AgentKitError(`Failed to update task ${taskId}`, res));
      setIsLoading(false);
      return null;
    }
    setIsLoading(false);
    return res;
  }, []);

  return { 
    createTask, 
    updateTask,
    isLoading, 
    error 
  };
};